<template>
  <div class="social-links-section">
    <div class="section-title">
      <span :style="'color: '+schoolColor">Réseaux sociaux</span>
    </div>

    <!-- Liste des liens sociaux -->
    <div v-if="socialLinks.length > 0" class="social-links-list">
      <div v-for="(link, index) in socialLinks" :key="index" class="social-link-item">
        <div class="social-link-content">
          <div class="platform-icon">
            <i :class="getPlatformIcon(link.platform)"></i>
          </div>
          <div class="link-details">
            <div class="platform-name">{{ getPlatformLabel(link.platform) }}</div>
            <a :href="link.url" target="_blank" class="profile-url">{{ link.url }}</a>
          </div>
        </div>
        <div class="link-actions">
          <button type="button" class="action-btn edit-btn" @click.stop.prevent="editSocialLink(index)" title="Modifier">
            <i class="fa-solid fa-pen"></i>
          </button>
          <button type="button" class="action-btn delete-btn" @click.stop.prevent="removeSocialLink(index)" title="Supprimer">
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Formulaire d'ajout/modification -->
    <div class="social-form">
      <div class="form-group platform-select">
        <label>Plateforme</label>
        <div class="select-wrapper">
          <select v-model="currentLink.platform" @change="onPlatformChange">
            <option value="" disabled>Sélectionner une plateforme</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="linkedin">LinkedIn</option>
            <option value="other">Autre</option>
          </select>
          <i class="fa-solid fa-chevron-down select-arrow"></i>
        </div>
        <p v-if="formErrors.platform" class="error-message">{{ formErrors.platform }}</p>
      </div>

      <div v-if="currentLink.platform === 'other'" class="form-group platform-name">
        <label>Nom de la plateforme</label>
        <input type="text" v-model="currentLink.platform_name" placeholder="ex: Facebook, X, Snapchat, ..." />
        <p v-if="formErrors.platform_name" class="error-message">{{ formErrors.platform_name }}</p>
      </div>

      <div class="form-row">
        <div class="form-group url-input">
          <label>URL du profil</label>
          <input type="url" v-model="currentLink.url" :placeholder="getUrlPlaceholder(currentLink.platform)" />
          <p v-if="formErrors.url" class="error-message">{{ formErrors.url }}</p>
        </div>

        <div class="form-group username-input">
          <label>Nom d'utilisateur</label>
          <input type="text" v-model="currentLink.username" :placeholder="getUsernamePlaceholder(currentLink.platform)" />
        </div>
      </div>

      <div class="form-actions">
        <button v-if="editMode" type="button" class="cancel-btn" @click="cancelEdit">Annuler</button>
        <button type="button" class="add-btn" :class="{'update-btn': editMode}" @click="saveSocialLink">
          {{ editMode ? 'Mettre à jour' : 'Ajouter' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    candidateSocialLinks: {
      type: Array,
      default: () => []
    },
    schoolColor: {
      type: String,
      default: '#051527'
    }
  },

  data() {
    return {
      socialLinks: [],
      currentLink: this.getEmptyLink(),
      editMode: false,
      editIndex: -1,
      formErrors: {}
    }
  },

  created() {
    // Copie des liens sociaux passés en prop
    this.socialLinks = [...this.candidateSocialLinks];
  },

  methods: {
    getEmptyLink() {
      return {
        platform: '',
        platform_name: '',
        url: '',
        username: ''
      }
    },

    getPlatformIcon(platform) {
      const icons = {
        'instagram': 'fa-brands fa-instagram',
        'tiktok': 'fa-brands fa-tiktok',
        'linkedin': 'fa-brands fa-linkedin',
        'other': 'fa-solid fa-link'
      };
      return icons[platform] || 'fa-solid fa-link';
    },

    getPlatformLabel(platform) {
      if (platform === 'other' && this.currentLink.platform_name) {
        return this.currentLink.platform_name;
      }

      const labels = {
        'instagram': 'Instagram',
        'tiktok': 'TikTok',
        'linkedin': 'LinkedIn',
        'other': 'Autre'
      };
      return labels[platform] || platform;
    },

    getUrlPlaceholder(platform) {
      const placeholders = {
        'instagram': 'https://www.instagram.com/username',
        'tiktok': 'https://www.tiktok.com/@username',
        'linkedin': 'https://www.linkedin.com/in/username',
        'other': 'https://...'
      };
      return placeholders[platform] || 'https://...';
    },

    getUsernamePlaceholder(platform) {
      const placeholders = {
        'instagram': '@username',
        'tiktok': '@username',
        'linkedin': 'prenom-nom',
        'other': 'username'
      };
      return placeholders[platform] || 'username';
    },

    onPlatformChange() {
      // Réinitialiser les erreurs quand la plateforme change
      this.formErrors = {};
    },

    validateLink() {
      let isValid = true;
      this.formErrors = {};

      if (!this.currentLink.platform) {
        this.formErrors.platform = 'Veuillez sélectionner une plateforme';
        isValid = false;
      }

      if (this.currentLink.platform === 'other' && !this.currentLink.platform_name) {
        this.formErrors.platform_name = 'Veuillez spécifier le nom de la plateforme';
        isValid = false;
      }

      if (!this.currentLink.url) {
        this.formErrors.url = 'Veuillez saisir l\'URL du profil';
        isValid = false;
      } else if (!this.isValidUrl(this.currentLink.url)) {
        this.formErrors.url = 'Veuillez saisir une URL valide';
        isValid = false;
      } else {
        // Validation spécifique selon la plateforme
        if (this.currentLink.platform === 'instagram') {
          if (!this.validateInstagramUrl(this.currentLink.url)) {
            this.formErrors.url = 'Cette URL ne semble pas être un profil Instagram valide';
            isValid = false;
          }
        } else if (this.currentLink.platform === 'tiktok') {
          if (!this.validateTikTokUrl(this.currentLink.url)) {
            this.formErrors.url = 'Cette URL ne semble pas être un profil TikTok valide';
            isValid = false;
          }
        } else if (this.currentLink.platform === 'linkedin') {
          if (!this.validateLinkedInUrl(this.currentLink.url)) {
            this.formErrors.url = 'Cette URL ne semble pas être un profil LinkedIn valide';
            isValid = false;
          }
        }
      }

      return isValid;
    },

    isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch (e) {
        return false;
      }
    },

    validateInstagramUrl(url) {
      // Formats acceptés :
      // https://www.instagram.com/username/
      // https://instagram.com/username/
      const regex = /^https?:\/\/(www\.)?instagram\.com\/([A-Za-z0-9_](?:(?:[A-Za-z0-9_]|(?:\.(?!\.))){0,28}(?:[A-Za-z0-9_]))?)(?:\/)?$/;
      return regex.test(url);
    },

    validateTikTokUrl(url) {
      // Formats acceptés :
      // https://www.tiktok.com/@username
      // https://tiktok.com/@username
      const regex = /^https?:\/\/(www\.)?tiktok\.com\/@([A-Za-z0-9_\.-]{1,24})(?:\/)?$/;
      return regex.test(url);
    },

    validateLinkedInUrl(url) {
      // Formats acceptés :
      // https://www.linkedin.com/in/username
      // https://linkedin.com/in/username
      // https://www.linkedin.com/company/company-name
      const regex = /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/([A-Za-z0-9\-]{1,100})(?:\/)?$/;
      return regex.test(url);
    },

    editSocialLink(index) {
      this.editMode = true;
      this.editIndex = index;
      this.currentLink = { ...this.socialLinks[index] };
    },

    removeSocialLink(index) {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce réseau social?')) {
        this.socialLinks.splice(index, 1);
        this.$emit('update:socialLinks', this.socialLinks);
      }
    },

    saveSocialLink() {
      if (!this.validateLink()) return;

      if (this.editMode) {
        // Mise à jour d'un lien existant
        this.socialLinks.splice(this.editIndex, 1, { ...this.currentLink });
      } else {
        // Ajout d'un nouveau lien
        this.socialLinks.push({ ...this.currentLink });
      }

      // Émettre l'événement pour mettre à jour les données du parent
      this.$emit('update:socialLinks', this.socialLinks);

      // Réinitialiser le formulaire
      this.currentLink = this.getEmptyLink();
      this.editMode = false;
      this.editIndex = -1;
    },

    cancelEdit() {
      this.currentLink = this.getEmptyLink();
      this.editMode = false;
      this.editIndex = -1;
      this.formErrors = {};
    }
  }
}
</script>

<style scoped>
.social-links-section {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  margin-bottom: 1.5rem;
  color: #333;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

/* Liste des liens */
.social-links-list {
  margin-bottom: 1rem;
}

.social-link-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.social-link-item:hover {
  background-color: #f0f0f0;
}

.social-link-content {
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0; /* Permet aux enfants de rétrécir correctement */
}

.platform-icon {
  font-size: 1.25rem;
  width: 2rem;
  text-align: center;
  color: #666;
  margin-top: 0.25rem;
}

.link-details {
  flex: 1;
  min-width: 0; /* Permet aux enfants de rétrécir correctement */
  padding-left: 0.5rem;
  overflow: hidden;
}

.platform-name {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.1rem;
}

.profile-url {
  font-size: 0.85rem;
  color: #007bff;
  text-decoration: none;
  word-break: break-all;
  display: inline-block;
  max-width: 100%;
}

.profile-url:hover {
  text-decoration: underline;
}

.link-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: 0.5rem;
}

.action-btn {
  background: none;
  border: none;
  font-size: 0.85rem;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.edit-btn {
  color: #4285F4;
  background-color: rgba(66, 133, 244, 0.1);
}

.edit-btn:hover {
  background-color: rgba(66, 133, 244, 0.2);
}

.delete-btn {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.delete-btn:hover {
  background-color: rgba(220, 53, 69, 0.2);
}

/* Formulaire */
.social-form {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  flex: 1;
  min-width: 0; /* Permet de rétrécir correctement */
  margin-bottom: 1rem;
}

.platform-select {
  min-width: 200px;
}

.url-input {
  flex: 2;
}

label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: #555;
}

.optional {
  font-weight: normal;
  color: #888;
  font-size: 0.8rem;
}

input, select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
}

input:focus, select:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.select-wrapper {
  position: relative;
}

.select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
}

select {
  appearance: none;
  padding-right: 25px; /* Espace pour la flèche */
}

.error-message {
  font-size: 0.8rem;
  color: #dc3545;
  margin-top: 0.3rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.add-btn, .cancel-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn {
  background-color: #4285F4;
  color: white;
}

.add-btn:hover {
  background-color: #3367d6;
}

.update-btn {
  background-color: #4285F4;
}

.update-btn:hover {
  background-color: #3367d6;
}

.cancel-btn {
  background-color: #f8f9fa;
  color: #333;
  border: 1px solid #ddd;
}

.cancel-btn:hover {
  background-color: #e9ecef;
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-group {
    width: 100%;
  }

  .social-link-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .link-actions {
    margin-top: 0.5rem;
    align-self: flex-end;
  }
}
</style>
